/**
 * RESPONSIBILITY: read stored text into a repeating date, or say why it is not one.
 *
 * Two spellings are read. The RRULE the picker writes — see repeat-rule.ts — and the cron
 * expression this field held before it, so an event saved earlier keeps its rule. A cron
 * value is rewritten as RRULE by the next save (repeat-format.ts, canonicalRepeat); none
 * is ever written.
 */
import { parseCron } from './cron';
import { DAY_CODES, ORDINALS, type Ordinal, type RepeatFreq, type RepeatRule } from './repeat-rule';

const FREQS: Record<string, RepeatFreq> = { WEEKLY: 'weekly', MONTHLY: 'monthly', YEARLY: 'yearly' };
const KNOWN = new Set(['FREQ', 'BYDAY', 'BYMONTHDAY', 'BYMONTH', 'BYHOUR', 'BYMINUTE']);

const int = (s: string, lo: number, hi: number, what: string): number => {
  if (!/^-?\d+$/.test(s) || Number(s) < lo || Number(s) > hi) {
    throw new Error(`${what} must be ${lo}–${hi}`);
  }
  return Number(s);
};

/** `NAME=value;NAME=value` → a map, refusing anything this site does not write. */
function pairs(text: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const part of text.split(';')) {
    if (!part.trim()) continue;
    const [k, v, extra] = part.split('=');
    if (v === undefined || extra !== undefined) throw new Error(`"${part}" is not NAME=value`);
    const key = k.trim().toUpperCase();
    if (!KNOWN.has(key)) throw new Error(`${key} is not supported`);
    if (out.has(key)) throw new Error(`${key} is given twice`);
    out.set(key, v.trim().toUpperCase());
  }
  return out;
}

/** BYDAY → the days, and the count in front of a monthly one. */
function byDay(spec: string, freq: RepeatFreq, rule: RepeatRule): void {
  for (const d of spec.split(',')) {
    const m = /^(-?\d)?([A-Z]{2})$/.exec(d.trim());
    const day = m ? DAY_CODES.indexOf(m[2] as (typeof DAY_CODES)[number]) : -1;
    if (!m || day < 0) throw new Error(`"${d}" is not a day`);
    if (m[1] !== undefined) {
      const n = Number(m[1]);
      if (!ORDINALS.includes(n as Ordinal)) throw new Error('a weekday in the month is 1–4 or -1');
      if (rule.nth !== undefined) throw new Error('one weekday in the month at a time');
      rule.nth = n as Ordinal;
    }
    if (!rule.days.includes(day)) rule.days.push(day);
  }
  rule.days.sort((a, b) => a - b);

  if (freq === 'yearly') throw new Error('a yearly rule is a date, not a weekday');
  if (freq === 'weekly' && rule.nth !== undefined) throw new Error('a weekly rule does not count');
  if (freq === 'monthly' && (rule.nth === undefined || rule.days.length !== 1)) {
    throw new Error('a monthly weekday is one day with its count, e.g. 3TU');
  }
}

function parseRrule(text: string): RepeatRule {
  const p = pairs(text);
  const freq = FREQS[p.get('FREQ') ?? ''];
  if (!freq) throw new Error('FREQ must be WEEKLY, MONTHLY or YEARLY');

  const rule: RepeatRule = { freq, days: [] };
  const day = p.get('BYDAY');
  if (day) byDay(day, freq, rule);

  const monthDay = p.get('BYMONTHDAY');
  if (monthDay !== undefined) {
    if (freq === 'weekly') throw new Error('a weekly rule has no date');
    if (rule.nth !== undefined) throw new Error('a weekday or a date, not both');
    rule.monthDay = int(monthDay, 1, 31, 'the day of the month');
  }

  const month = p.get('BYMONTH');
  if (month !== undefined) {
    if (freq !== 'yearly') throw new Error('only a yearly rule names a month');
    rule.month = int(month, 1, 12, 'the month');
  }

  const h = p.get('BYHOUR');
  const mi = p.get('BYMINUTE');
  if ((h === undefined) !== (mi === undefined)) throw new Error('an hour needs its minute');
  if (h !== undefined && mi !== undefined) {
    rule.hour = int(h, 0, 23, 'the hour');
    rule.minute = int(mi, 0, 59, 'the minute');
  }
  return rule;
}

/**
 * The rule a stored cron expression meant, for the shapes this site ever saved. Anything
 * wider — every fifteen minutes, a range of hours — was never a church event, and is
 * refused rather than half-understood.
 */
function fromCron(text: string): RepeatRule {
  const c = parseCron(text);
  const one = (xs: number[], what: string): number => {
    if (xs.length !== 1) throw new Error(`a repeat has one ${what}`);
    return xs[0];
  };
  const time = { hour: one(c.hours, 'hour'), minute: one(c.minutes, 'minute') };

  if (c.nth !== undefined) {
    if (c.nth > 4) throw new Error('a weekday in the month is 1–4 or the last');
    if (c.daysOfMonth.length || c.months.length) throw new Error('a monthly weekday runs all year');
    return { freq: 'monthly', days: [one(c.daysOfWeek, 'weekday')], nth: c.nth as Ordinal, ...time };
  }
  if (c.daysOfMonth.length === 0) {
    if (c.months.length) throw new Error('a weekly repeat runs all year');
    // Every field but the time left as `*` is every day: weekly, on all seven.
    const days = c.daysOfWeek.length ? c.daysOfWeek : [0, 1, 2, 3, 4, 5, 6];
    return { freq: 'weekly', days, ...time };
  }
  if (c.daysOfWeek.length) throw new Error('a date or a weekday, not both');
  const monthDay = one(c.daysOfMonth, 'day of the month');
  if (c.months.length === 0) return { freq: 'monthly', days: [], monthDay, ...time };
  return { freq: 'yearly', days: [], month: one(c.months, 'month'), monthDay, ...time };
}

/** Throws, saying why, on anything that is not a rule. Empty text is not a rule either. */
export function parseRepeat(text: string): RepeatRule {
  const t = text.trim();
  if (!t) throw new Error('empty');
  return /^FREQ=/i.test(t) || t.includes(';') ? parseRrule(t) : fromCron(t);
}

/** The rule, or null when there is none or it cannot be read. */
export function readRepeat(text: string | undefined | null): RepeatRule | null {
  if (!text?.trim()) return null;
  try { return parseRepeat(text); } catch { return null; }
}

/** True when the text is a rule this site can read — for validating a write. */
export function isRepeat(text: string): boolean {
  try { parseRepeat(text); return true; } catch { return false; }
}

/** Why the text is not a rule, for the message an editor is shown; '' when it is one. */
export function repeatProblem(text: string): string {
  try { parseRepeat(text); return ''; } catch (e) { return (e as Error).message; }
}
